import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

interface ValidationResponse {
  valid: boolean;
  available: boolean;
  error?: string;
  suggestions?: string[];
}

interface ListingNameInputProps {
  value: string;
  onChange: (value: string) => void;
  listingId?: string;
  required?: boolean;
}

const ListingNameInput: React.FC<ListingNameInputProps> = ({
  value,
  onChange,
  listingId,
  required = true,
}) => {
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [touched, setTouched] = useState(false);

  // Validate name format locally (instant feedback)
  const validateFormat = (name: string): string | null => {
    if (!name) return null;
    
    const trimmed = name.trim();
    
    if (trimmed.length < 3) {
      return 'Listing name must be at least 3 characters long';
    }
    
    if (trimmed.length > 63) {
      return 'Listing name must be 63 characters or less';
    }
    
    if (!/^[a-z0-9-]+$/.test(trimmed)) {
      return 'Listing name must contain only lowercase letters, numbers, and hyphens. Example: my-cool-space';
    }
    
    if (trimmed.startsWith('-') || trimmed.endsWith('-')) {
      return 'Listing name cannot start or end with a hyphen';
    }
    
    if (trimmed.includes('--')) {
      return 'Listing name cannot contain consecutive hyphens';
    }
    
    if (/^\d+$/.test(trimmed)) {
      return 'Listing name cannot be all numbers. Please include letters or hyphens';
    }
    
    return null;
  };

  // Validate with server (debounced)
  const validateWithServer = useCallback(async (name: string) => {
    if (!name || !touched) return;
    
    // First check format locally
    const formatError = validateFormat(name);
    if (formatError) {
      setValidation({ valid: false, available: false, error: formatError });
      return;
    }
    
    setIsValidating(true);
    
    try {
      const params = new URLSearchParams({ name });
      if (listingId) {
        params.append('listingId', listingId);
      }
      
      const response = await api.get<ValidationResponse>(
        `/listings/validate-name?${params.toString()}`
      );
      
      setValidation(response);
    } catch (error) {
      console.error('Validation error:', error);
      setValidation({
        valid: false,
        available: false,
        error: 'Unable to validate name. Please try again.',
      });
    } finally {
      setIsValidating(false);
    }
  }, [listingId, touched]);

  // Debounced validation on value change
  useEffect(() => {
    if (!value || !touched) {
      return;
    }

    // Check format instantly
    const formatError = validateFormat(value);
    if (formatError) {
      setValidation({ valid: false, available: false, error: formatError });
      return;
    }
    
    // Debounce server validation (500ms)
    const timer = setTimeout(() => {
      void validateWithServer(value);
    }, 500);
    
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, touched]);

  // Validate on blur
  const handleBlur = () => {
    setTouched(true);
    if (value) {
      void validateWithServer(value);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toLowerCase();
    onChange(newValue);
  };

  const applySuggestion = (suggestion: string) => {
    onChange(suggestion);
    setTouched(true);
  };

  const getStatusIcon = () => {
    if (!touched || !value) return null;
    
    if (isValidating) {
      return (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      );
    }
    
    if (validation?.valid && validation?.available) {
      return (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    
    if (validation && (!validation.valid || !validation.available)) {
      return (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    
    return null;
  };

  const getBorderColor = () => {
    if (!touched || !value) return 'border-gray-300 focus:border-primary-500';
    
    if (validation?.valid && validation?.available) {
      return 'border-green-500 focus:border-green-600';
    }
    
    if (validation && (!validation.valid || !validation.available)) {
      return 'border-red-500 focus:border-red-600';
    }
    
    return 'border-gray-300 focus:border-primary-500';
  };

  const frontendUrl = window.location.origin;

  return (
    <div>
      <label htmlFor="listing-name" className="block text-sm font-medium text-gray-700 mb-1">
        Listing Name (URL) {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="text"
          id="listing-name"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          required={required}
          className={`block w-full px-3 py-2 pr-10 border ${getBorderColor()} rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 transition-colors`}
          placeholder="my-awesome-space"
        />
        {getStatusIcon()}
      </div>
      
      {/* URL Preview */}
      {value && (
        <div className="mt-2 text-sm text-gray-600">
          <span className="font-medium">Your listing URL:</span>{' '}
          <span className="text-primary-600">{frontendUrl}/{value}</span>
        </div>
      )}
      
      {/* Validation Messages */}
      {touched && validation && validation.error && (
        <div className="mt-2 text-sm text-red-600">
          {validation.error}
        </div>
      )}
      
      {/* Suggestions */}
      {touched && validation && !validation.available && validation.suggestions && validation.suggestions.length > 0 && (
        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm font-medium text-yellow-800 mb-2">Try these alternatives:</p>
          <div className="flex flex-wrap gap-2">
            {validation.suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => applySuggestion(suggestion)}
                className="px-3 py-1 text-sm bg-white border border-yellow-300 text-yellow-800 rounded-md hover:bg-yellow-50 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Helper Text */}
      {!validation && (
        <p className="mt-1 text-sm text-gray-500">
          Only lowercase letters, numbers, and hyphens (3-63 characters)
        </p>
      )}
    </div>
  );
};

export default ListingNameInput;

